"use client";

import Link from "next/link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    Legend,
} from "recharts";
import {
    FileText,
    Clock,
    AlertCircle,
    AlertTriangle,
    CheckCircle2,
    ArrowRight,
} from "lucide-react";
import {
    getDashboardStats,
    getCategoryChartData,
    getStatusChartData,
    getMonthlyTrendData,
    mockComplaints,
    getStatusConfig,
    mockCategories,
} from "@/lib/mock-data";
import { formatDateShort } from "@/lib/date-utils";

export default function StaffDashboardPage() {
    const stats = getDashboardStats();
    const categoryData = getCategoryChartData();
    const statusData = getStatusChartData();
    const trendData = getMonthlyTrendData();

    const statCards = [
        {
            title: "รอรับเรื่อง",
            value: stats.submitted,
            icon: FileText,
            color: "#64748b",
            bgColor: "#f1f5f9",
        },
        {
            title: "กำลังดำเนินการ",
            value: stats.inProgress,
            icon: Clock,
            color: "#ca8a04",
            bgColor: "#fef9c3",
        },
        {
            title: "รอข้อมูลเพิ่ม",
            value: stats.pendingInfo,
            icon: AlertCircle,
            color: "#ea580c",
            bgColor: "#ffedd5",
        },
        {
            title: "ใกล้เกิน SLA",
            value: stats.nearSla,
            icon: AlertTriangle,
            color: "#dc2626",
            bgColor: "#fee2e2",
        },
        {
            title: "แล้วเสร็จเดือนนี้",
            value: stats.resolvedThisMonth,
            icon: CheckCircle2,
            color: "#16a34a",
            bgColor: "#dcfce7",
        },
    ];

    const recentComplaints = mockComplaints.slice(0, 5);

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div>
                <h1 className="heading-sm text-primary">แดชบอร์ด</h1>
                <p className="text-muted-foreground">ภาพรวมเรื่องร้องเรียนสำหรับเจ้าหน้าที่</p>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {statCards.map((stat) => (
                    <Card key={stat.title}>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div
                                    className="h-12 w-12 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: stat.bgColor }}
                                >
                                    <stat.icon className="h-6 w-6" style={{ color: stat.color }} />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stat.value}</p>
                                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-6">
                {/* Category Bar Chart */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">เรื่องร้องเรียนแยกตามประเภท</CardTitle>
                        <CardDescription>จำนวนเรื่องในแต่ละประเภท</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={categoryData} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                    <XAxis type="number" />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        width={120}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <Tooltip />
                                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                                        {categoryData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Status Pie Chart */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">สถานะปัจจุบัน</CardTitle>
                        <CardDescription>การกระจายตามสถานะ</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={statusData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={2}
                                        dataKey="value"
                                        label={({ name, value }) => (value > 0 ? `${name}: ${value}` : "")}
                                        labelLine={false}
                                    >
                                        {statusData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Trend Chart */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">แนวโน้มรายเดือน</CardTitle>
                    <CardDescription>จำนวนเรื่องที่รับและปิดในแต่ละเดือน</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="received"
                                    name="รับเรื่อง"
                                    stroke="#1e3a5f"
                                    strokeWidth={2}
                                    dot={{ fill: "#1e3a5f" }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="closed"
                                    name="ปิดเรื่อง"
                                    stroke="#16a34a"
                                    strokeWidth={2}
                                    dot={{ fill: "#16a34a" }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Recent Complaints Table */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-base">เรื่องร้องเรียนล่าสุด</CardTitle>
                        <CardDescription>5 เรื่องล่าสุดที่เข้ามาในระบบ</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                        <Link href="/staff/complaints">
                            ดูทั้งหมด
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                    </Button>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>เลขที่อ้างอิง</TableHead>
                                <TableHead className="hidden md:table-cell">วันที่</TableHead>
                                <TableHead>เรื่อง</TableHead>
                                <TableHead className="hidden sm:table-cell">ประเภท</TableHead>
                                <TableHead>สถานะ</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {recentComplaints.map((complaint) => {
                                const statusConfig = getStatusConfig(complaint.status);
                                const category = mockCategories.find((c) => c.id === complaint.categoryId);
                                return (
                                    <TableRow key={complaint.id}>
                                        <TableCell>
                                            <Link
                                                href={`/staff/complaints/${complaint.id}`}
                                                className="font-medium text-primary hover:underline"
                                            >
                                                {complaint.referenceNumber}
                                            </Link>
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell text-muted-foreground">
                                            {formatDateShort(complaint.createdAt)}
                                        </TableCell>
                                        <TableCell className="max-w-[200px] truncate">
                                            {complaint.subject}
                                        </TableCell>
                                        <TableCell className="hidden sm:table-cell text-muted-foreground text-sm">
                                            {category?.name.substring(0, 15)}...
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant="secondary"
                                                style={{
                                                    backgroundColor: statusConfig.bgColor,
                                                    color: statusConfig.darkColor,
                                                }}
                                            >
                                                {statusConfig.label}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
